import { useEffect, useRef, useState } from 'react';
import { Share2, AlertTriangle, Loader2, Copy, Check, Archive } from 'lucide-react';
import { formatarLinhaDigitavel, referenciaDoBoleto, lerLinhaDigitavel, formatCpfCnpj } from '@fiodecobre/shared';
import {
  appApi, caminhoPdfNota, caminhoPdfBoleto, caminhoPacoteNotas, caminhoPacoteEmAberto,
  getTokenApp, ErroApp,
  type FinanceiroApp as Dados, type NotaApp, type NotaAcervoApp, type NotasFiscaisApp,
  type MesDeNotasApp, type BoletoApp, type BoletosApp,
} from './api';

// AppCel — "Financeiro": TRES abas, e elas respondem perguntas DIFERENTES.
//
//   • **Boletos** ([DA-629], 27/08/2026) — *"como eu pago?"* O PDF que a loja subiu
//     mais a linha digitavel, com botao de copiar. Amarrado ao titulo: sai da lista
//     sozinho quando a loja baixa a conta.
//
//   • **Notas Fiscais** (a que ABRE) — *"cade a nota daquela compra?"*, inclusive
//     ja paga. Foi a medicao que justificou a aba: **11 das 12 contas com ficha
//     nao devem nada**, e 7 dessas 11 tem notas (TELO 45, RONAN 34, RAFAEL 16,
//     GALA 15, PAULO 13, ZE WILTON 8, HATILLA 6). Sem ela, a area inteira era uma
//     sala vazia pra quase todo mundo.
//   • **Contas a Pagar** — *"quanto eu devo, e cade a nota disso?"* Recorte duro,
//     escolhido pelo Ruan em 20/08/2026 ([DA-590]): so titulo em aberto, e so a
//     nota dele.
//
// ⚠️ **Duas ABAS, e nao dois itens de menu** — decisao dele, e a casa ja falava
// esse dialeto no `OrcamentosApp` ("Meus orçamentos | Modelos").
//
// ⚠️ **A ordem foi corrigida por ele no mesmo dia, DEPOIS de ver a tela**: subiu
// com Contas a Pagar primeiro (o nome antigo da area puxou a ordem antiga) e ele
// inverteu. A regra que fica: **a primeira aba e' a que abre, e abrir e' o que
// decide o que o cliente encontra sem procurar** — colocar na frente a aba que
// esta vazia pra 11 de 12 era esconder atras de um toque justamente o que ele
// veio buscar. Nao era ordem de lista: era o padrao de abertura.
//
// ⚠️ **Esta e' a primeira tela do aplicativo que mostra dinheiro.** Duas coisas
// vieram disso:
//
//  1. **Nenhum numero e' calculado aqui.** Total em aberto, dias de atraso e o selo
//     de vencido chegam prontos do servidor. O relogio do celular pode estar errado
//     — e uma conta que diz "vencida" por causa do fuso do aparelho e' pior que nao
//     dizer nada.
//  2. **Cor nunca carrega o sinal sozinha.** O vencido e' laranja *e* diz "Vencido
//     ha N dias" por escrito. Verde e vermelho nao servem de sinal nesta casa.
//
// ⛔ O que esta tela NAO faz: pagar. Decisao do Ruan em 16/08/2026 — ver a conversa
// da onda. Botao de pagar mexe em conciliacao e dobra o tamanho disto.

const COBRE = '#b45309';

/** ⚠️ Lado do botao de compartilhar, e ele e' quadrado. O desenho que ele mandou
 *  pede um botao discreto ao lado do valor, nao um bloco — 36 e' o meio-termo entre
 *  aquele desenho e um alvo que o dedo acerta; abaixo disso o toque comeca a errar
 *  num celular. **O mesmo numero reserva o espaco da linha sem nota**, entao mexer
 *  aqui mantem a coluna de valores alinhada sozinha. */
const TAM_BOTAO = 36;
const LARANJA = '#c2410c';
const CINZA = '#78716c';
const BORDA = '#e7e5e4';

function fmtMoney(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** ⚠️ Vencimento e' `date` puro (YYYY-MM-DD). Montar `new Date(bruto)` cai em UTC e
 *  volta UM DIA — a conta apareceria vencendo na vespera. Por isso a data pura e'
 *  fatiada como texto, e so o que tem hora vira `Date`. */
function fmtData(bruto: string | null): string {
  if (!bruto) return '—';
  const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bruto);
  if (soData) return `${soData[3]}/${soData[2]}/${soData[1]}`;
  const d = new Date(bruto);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

type Aba = 'notas' | 'contas' | 'boletos';

/**
 * A ordem e' escolha DELE (27/08/2026), e ela nao e' estetica: **a primeira aba e' a
 * que abre**, e abrir decide o que o cliente encontra sem procurar. Notas Fiscais
 * segue na frente pelo mesmo numero da [DA-627] — 11 das 12 contas com ficha nao
 * devem nada —, e Boletos entra por ultimo porque hoje ele alcanca **uma** conta:
 * so a ILUMINAR tem titulo em aberto (medido em 27/08: 17 titulos, R$ 5.571,50).
 */
const ABAS: ReadonlyArray<readonly [Aba, string]> = [
  ['notas', 'Notas Fiscais'],
  ['contas', 'Contas a Pagar'],
  ['boletos', 'Boletos'],
];

export function FinanceiroApp(): React.ReactElement {
  // ⛔ **NOTAS FISCAIS PRIMEIRO, e e' a aba que ABRE** (decisao dele, 26/08/2026,
  // depois de ver a tela publicada). A ordem do menu nao e' estetica: a primeira
  // aba e' a que abre, e abrir e' o que decide o que o cliente encontra sem
  // procurar. Os numeros davam razao a ele — **11 das 12 contas com ficha nao
  // devem nada**, entao abrir no Contas a Pagar era abrir numa tela vazia pra
  // quase todo mundo, com o que ele veio buscar escondido atras de um toque.
  const [aba, setAba] = useState<Aba>('notas');

  return (
    <div style={{ paddingBottom: 8 }}>
      {/* Abas e nao itens de menu (decisao dele, 26/08/2026) — mesmo desenho do
          `OrcamentosApp`, pro app nao mudar de gramatica de tela pra tela.
          ⚠️ A barra aparece ANTES do dado chegar: escondê-la enquanto carrega faria
          a tela pular e tiraria do cliente a informacao de que as outras existem.
          ⚠️ **Com TRES abas o texto passou a nao caber em celular estreito** — daí o
          `fontSize` menor e o `padding: 0 2px`. ⛔ Nao trocar por reticencias: rotulo
          de aba cortado ("Contas a P...") esconde justamente a palavra que distingue
          uma aba da outra. */}
      <div style={{ display: 'flex', backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
        {ABAS.map(([a, rotulo]) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            style={{
              flex: 1, height: 46, fontSize: 13.5, fontWeight: aba === a ? 800 : 600,
              padding: '0 2px', whiteSpace: 'nowrap',
              color: aba === a ? COBRE : CINZA,
              borderBottom: aba === a ? `2px solid ${COBRE}` : '2px solid transparent',
              backgroundColor: 'transparent',
            }}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'notas' ? <SecaoNotas /> : aba === 'contas' ? <SecaoContas /> : <SecaoBoletos />}
    </div>
  );
}

/**
 * A aba "Contas a Pagar" — e ela agora CARREGA SOZINHA, como a irma.
 *
 * ⚠️ **A carga teve que inverter junto com a ordem.** Enquanto esta era a aba de
 * abertura, buscar `/financeiro` na montagem da tela era o certo. Com a abertura em
 * Notas Fiscais, aquela chamada viraria uma ida ao servidor que ninguem pediu — em
 * TODA abertura, e num celular no meio de uma obra. Trocar qual aba abre sem trocar
 * quem carrega o que e' o jeito de deixar uma consulta orfa rodando pra sempre.
 *
 * ⚠️ Efeito colateral deliberado na SONDA: `GET /api/app/financeiro` deixou de
 * acontecer na abertura, entao a area `financeiro` do relatorio de uso passa a
 * contar **quem realmente tocou nesta aba**, e nao quem abriu o Financeiro. O
 * numero fica mais honesto, mas MUDA DE SIGNIFICADO em 26/08/2026 — a leitura da
 * serie em 02/09 tem que saber disso antes de comparar antes e depois.
 */
function SecaoContas(): React.ReactElement {
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    appApi
      .financeiro()
      .then((d) => { if (vivo) setDados(d); })
      .catch((e: ErroApp) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, []);

  if (erro) return <Aviso texto={erro} />;
  if (!dados) return <Carregando />;
  return <SecaoTitulos dados={dados} />;
}

/**
 * O aviso que ocupa o lugar da lista quando a consulta nao volta.
 *
 * ⚠️ Na pratica ele e' o `sem_ficha` (403), que vale pras DUAS abas — as duas
 * dependem da mesma ficha. Fica DENTRO da aba (e nao no lugar da tela inteira)
 * porque as abas agora carregam cada uma a sua: esconder a barra tiraria do
 * cliente a unica pista de que ha outra coisa ali, e ele leria como app quebrado.
 */
function Aviso({ texto }: { texto: string }): React.ReactElement {
  return (
    <div style={{ padding: 16 }}>
      <div style={{ backgroundColor: '#fff8ef', border: '1px solid #f0d5b0', borderRadius: 10, padding: 14, fontSize: 14, color: '#5b4636', lineHeight: 1.5 }}>
        {texto}
      </div>
    </div>
  );
}

function Carregando(): React.ReactElement {
  return <div style={{ padding: 20, fontSize: 14, color: '#6b7280' }}>Carregando...</div>;
}

function Cabecalho({ texto }: { texto: string }): React.ReactElement {
  return (
    <div style={{ padding: '16px 16px 8px', fontSize: 12.5, fontWeight: 800, letterSpacing: 0.6, color: '#78716c', textTransform: 'uppercase' }}>
      {texto}
    </div>
  );
}

function SecaoTitulos({ dados }: { dados: Dados }): React.ReactElement {
  const { titulos, total_aberto, vencidos, notas_no_pacote } = dados;

  if (!titulos.length) {
    return (
      <>
        <Cabecalho texto="Em aberto" />
        {/* ⚠️ **A ponte pra aba vizinha e' MAIS necessaria aqui, nao menos** — e' o
            estado de **11 das 12 contas com ficha** (medido 26/08/2026). Na primeira
            versao a frase morava so no caminho COM titulos, e o caso majoritario
            terminava num "voce nao deve nada" que le como area vazia em quem tem 45
            notas ali do lado. Achado ABRINDO a tela publicada, nao lendo o codigo. */}
        <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: '20px 16px', fontSize: 15, color: '#44403c', lineHeight: 1.5 }}>
          Você não tem nada em aberto com a loja.
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
            Suas notas fiscais estão na aba <b>Notas Fiscais</b>, aqui em cima.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Cabecalho texto="Em aberto" />

      {/* O total no topo e' a excecao consciente a regra de "sem cards de resumo":
          numa lista de quanto se DEVE, o total e' a pergunta, e cada linha e' o
          detalhe dela. */}
      <div style={{ backgroundColor: '#fff', borderTop: '1px solid #e7e5e4', borderBottom: '1px solid #e7e5e4', padding: '14px 16px' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Total em aberto</div>
        {/* ⚠️ Sem `tabular-nums` AQUI, de proposito — e' o contrario da lista abaixo.
            A largura fixa serve pra alinhar uma COLUNA de valores; num numero grande
            e sozinho ela so poe ar em volta do ponto e da virgula ("R$ 4 . 223 , 00"),
            e este e' o numero que a pessoa abriu a tela pra ler. Medido na tela
            publicada, nao deduzido. */}
        <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginTop: 2 }}>
          R$ {fmtMoney(total_aberto)}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          {titulos.length === 1 ? '1 título' : `${titulos.length} títulos`}
          {vencidos > 0 && (
            <span style={{ color: LARANJA, fontWeight: 700 }}>
              {' · '}{vencidos === 1 ? '1 vencido' : `${vencidos} vencidos`}
            </span>
          )}
        </div>
      </div>

      {/* 📦 **"Baixar Notas" — pedido dele em 10/09/2026, com print marcando o lugar.**
          ⛔ **E' o MESMO componente do botao da aba Notas Fiscais**, por instrucao
          literal: *"o botao deve ser exatamente igual a esse. Tudo igual"*. Por isso
          largura total aqui embaixo, e nao o botao curto ao lado do total que eu tinha
          desenhado primeiro — ele viu o mockup e apontou o irmao como o modelo.
          ⚠️ **Sem o nome do mes no rotulo** (correcao dele na mesma conversa): o
          recorte deste pacote NAO e' um mes, e' o que ele deve — e o conjunto atravessa
          meses (o GRUPO SOLAR FACIL deve 1 nota de agosto e 3 de setembro).
          ⛔ **Nao aparece quando nao ha nota**: titulo em aberto sem nota emitida e'
          caso real (consignacao, legado), e botao que so' sabe recusar e' pior que
          botao nenhum. */}
      {notas_no_pacote > 0 && (
        <div style={{ padding: '12px 16px 0' }}>
          <BotaoPacote
            caminho={caminhoPacoteEmAberto()}
            nomeArquivo="notas-fiscais-em-aberto.zip"
            tituloCompartilhar="Notas fiscais das contas em aberto"
            rotulo={notas_no_pacote === 1
              ? 'Baixar a nota (.zip)'
              : `Baixar as ${notas_no_pacote} notas (.zip)`}
            rotuloEnviar={notas_no_pacote === 1
              ? 'Enviar a nota'
              : `Enviar as ${notas_no_pacote} notas`}
          />
        </div>
      )}

      <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e7e5e4', marginTop: 12 }}>
        {titulos.map((t) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '13px 12px 13px 16px', borderTop: '1px solid #f0efee' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, color: '#111' }}>
                Vence em {fmtData(t.data_vencimento)}
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                {t.orcamento_numero ? `Pedido ${t.orcamento_numero}` : `Título ${t.id}`}
              </div>
              {/* O significado vem do TEXTO; a cor so reforca. */}
              {t.vencido && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12.5, fontWeight: 700, color: LARANJA }}>
                  <AlertTriangle size={13} />
                  {t.dias_atraso === 1 ? 'Vencido há 1 dia' : `Vencido há ${t.dias_atraso} dias`}
                </div>
              )}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              R$ {fmtMoney(t.valor)}
            </div>
            {/* ⚠️ **O espaco e' reservado mesmo sem nota.** Titulo pendente sem nota
                emitida e' caso real (9 dos 54 medidos), e deixar a linha encolher
                faria a coluna de valores dancar de uma linha pra outra — o olho le
                isso como numero desalinhado, nao como "esta faltando a nota". */}
            <BotaoNota nota={t.nota} />
          </div>
        ))}
      </div>

      {/* ⚠️ **Esta frase mudou junto com a aba, e tinha que mudar** (26/08/2026).
          Ate ontem ela mandava pedir a nota de uma compra ja paga PELO CHAT, porque
          nao havia outro caminho; com a aba ao lado, o mesmo texto passaria a mandar
          o cliente pedir a um humano o que ele resolve num toque. Texto que descreve
          o mundo de antes nao fica "so desatualizado": ele desvia de um recurso que
          existe. */}
      <div style={{ padding: '10px 16px 2px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
        Para pagar ou combinar prazo, fale com a gente pelo chat. A nota de uma compra já paga
        está na aba <b>Notas Fiscais</b>.
      </div>
    </>
  );
}

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'] as const;
const MES_LONGO = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
] as const;

/** `2026-08` → `ago/26`. Rotulo de pastilha: cabe na horizontal e nao abrevia o ANO,
 *  que e' o que distingue agosto de agosto. */
function rotuloMesCurto(mes: string): string {
  return `${MES_CURTO[Number(mes.slice(5)) - 1] ?? mes.slice(5)}/${mes.slice(2, 4)}`;
}

/** `2026-08` → `agosto`. O nome que vai no botao, do jeito que ele escreveu o pedido. */
function rotuloMesLongo(mes: string): string {
  return MES_LONGO[Number(mes.slice(5)) - 1] ?? mes;
}

/**
 * A aba "Notas Fiscais" — o ACERVO (26/08/2026), com o seletor de mes e o pacote
 * .zip (01/09/2026).
 *
 * ⚠️ **Carrega sozinha, como a irma.** Desde 26/08/2026 esta e' a aba que ABRE,
 * entao esta consulta e' a unica que a tela dispara sem ninguem pedir — e a do
 * Contas a Pagar so sai quando alguem toca la.
 *
 * ⛔ **A tela esta SEMPRE num mes, e abre no mais recente** (decisao dele em
 * 01/09/2026, depois de ver a tela publicada com as pastilhas). A pastilha "Todas"
 * saiu: o acervo inteiro numa lista so era o que essa aba ja fazia, e o que ele quer
 * ao abrir e' o mes corrente em 9 de 10 vezes.
 *
 * ⚠️ **Sao DUAS chamadas ao abrir, e o motivo e' o fuso.** O celular nao sabe qual e'
 * o mes mais recente antes de perguntar, entao a primeira chamada vai sem filtro so
 * pra trazer a lista de `meses`; a segunda ja vem filtrada. ⛔ **Nao "otimizar" isso
 * separando as notas por mes aqui**: o mes de uma nota sai de `emitido_em`, que e'
 * `timestamptz`, e `new Date(iso).getMonth()` usa o fuso do APARELHO — uma nota das
 * 22h do dia 31 cairia no mes seguinte num celular com o relogio em outro fuso.
 * Quem tem o `AT TIME ZONE 'America/Sao_Paulo'` e' o servidor, e e' de la que o
 * agrupamento tem que vir. E' a mesma regra que o `fmtData` aqui em cima ja obedece.
 *
 * ⚠️ **Os meses vem do servidor e ficam guardados a parte** (`meses`): a lista de
 * notas troca a cada filtro, e derivar as pastilhas dela deixaria uma pastilha so na
 * tela depois do primeiro toque.
 */
function SecaoNotas(): React.ReactElement {
  const [mes, setMes] = useState<string | null>(null);
  const [dados, setDados] = useState<NotasFiscaisApp | null>(null);
  const [meses, setMeses] = useState<MesDeNotasApp[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    appApi
      .notasFiscais(mes)
      .then((d) => {
        if (!vivo) return;
        setMeses(d.meses);
        // Primeira carga: o celular pediu tudo porque ainda nao sabia quais meses
        // existem. Agora sabe — e a lista que vale e' a do mes mais recente, nao
        // esta. O `carregando` fica DE PE ate a segunda resposta chegar, senao a
        // tela pisca o acervo inteiro antes de trocar pelo mes.
        if (mes === null && d.meses.length > 0) { setMes(d.meses[0]!.mes); return; }
        setDados(d);
        setCarregando(false);
      })
      .catch((e: ErroApp) => { if (!vivo) return; setErro(e.message); setCarregando(false); });
    return () => { vivo = false; };
  }, [mes]);

  if (erro) return <Aviso texto={erro} />;
  if (!dados) return <Carregando />;

  if (!meses.length && !dados.notas.length) {
    return (
      <>
        <Cabecalho texto="Suas notas" />
        <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: '20px 16px', fontSize: 15, color: '#44403c', lineHeight: 1.5 }}>
          Ainda não há nota fiscal em seu nome aqui na loja.
        </div>
      </>
    );
  }

  // ⚠️ **O corte das 50 tem que ser VISIVEL.** Dentro de um mes ele praticamente nao
  // acontece — o mes mais cheio medido tem 35 notas —, mas ele CONTINUA POSSIVEL: o
  // `LIMITE_NOTAS` do servidor segue valendo sobre a fatia filtrada, e nada impede um
  // cliente de passar de 50 notas num mes. Por isso a frase fica. Texto so sai da
  // tela quando o caso que ele descreve deixa de existir; aqui ele so ficou raro.
  const cortou = dados.total > dados.notas.length;

  // ⛔ **So os 4 meses mais recentes** (decisao dele, 01/09/2026). E o corte tem que
  // ser VISIVEL, mesma regra do corte das 50 logo acima: hoje so o RONAN TEC tem 5
  // meses e perde mai/26, mas isto ROLA sozinho — em outubro a ILUMINAR passa a
  // perder um mes tambem. A linha nao e' pro caso raro de hoje, e' pro mes que vem.
  const MESES_NA_TELA = 4;
  const mesesVisiveis = meses.slice(0, MESES_NA_TELA);
  const cortouMeses = meses.length > mesesVisiveis.length;

  return (
    <>
      <Cabecalho texto="Suas notas" />

      <SeletorDeMes meses={mesesVisiveis} mes={mes} onEscolher={setMes} />

      {/* ⚠️ A frase mora COLADA no que ela descreve — o corte e' das pastilhas, nao
          da lista, entao ela nao pode ir pro rodape junto com o aviso das 50. E ela
          nomeia a PORTA (o chat): corte que so avisa deixa o cliente sabendo que
          falta algo e sem o que fazer a respeito. */}
      {cortouMeses && (
        <div style={{ padding: '0 16px 10px', fontSize: 12, color: '#78716c', lineHeight: 1.4 }}>
          Mostrando os {mesesVisiveis.length} meses mais recentes. Para uma nota mais antiga, fale com a gente pelo chat.
        </div>
      )}

      {mes && <PacoteDoMes meses={mesesVisiveis} mes={mes} />}

      {carregando && <Carregando />}

      <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, opacity: carregando ? 0.5 : 1 }}>
        {dados.notas.map((n) => (
          <div key={`${n.tipo}-${n.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '13px 12px 13px 16px', borderTop: `1px solid #f0efee` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, color: '#111' }}>
                Nota {n.numero}
              </div>
              {/* ⚠️ O TIPO vai escrito porque o cliente enxerga a diferenca no papel
                  (a DANFE e' de compra faturada, o cupom e' da venda no balcao) e
                  porque o mesmo NUMERO pode existir nas duas series — sem ele, duas
                  linhas ficariam com o mesmo rotulo. */}
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                {fmtData(n.emitido_em)} · {n.tipo === 'nfe' ? 'NF-e' : 'Cupom fiscal'}
              </div>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              R$ {fmtMoney(n.valor)}
            </div>
            <BotaoNota nota={n} />
          </div>
        ))}
      </div>

      {/* ⚠️ **A frase foi reescrita com a saida da pastilha "Todas"** (01/09/2026).
          Ela dizia "toque em Todas para ver o resto", apontando pra um botao que
          deixou de existir — e recusa/instrucao que nomeia porta inexistente e' pior
          que nao dizer nada ([DA-560]). O "resto" agora sao as outras pastilhas, e
          o que esta fora DELAS ja tem aviso proprio la em cima.
          ⚠️ E a versao "todas as suas notas fiscais na loja" saiu junto: com a tela
          sempre num mes, ela nunca mais seria verdade. */}
      <div style={{ padding: '10px 16px 2px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
        {cortou ? (
          <>Mostrando as <b>{dados.notas.length} mais recentes</b> de {dados.total}. Para uma nota mais antiga, fale com a gente pelo chat.</>
        ) : mes ? (
          <>Estas são as suas notas de <b>{rotuloMesLongo(mes)}</b>.</>
        ) : null}
      </div>
    </>
  );
}

/**
 * As pastilhas de mes — os 4 meses mais recentes que o cliente TEM (01/09/2026).
 *
 * ⛔ **So mes que existe.** Desenhar os 12 do ano ofereceria pastilha que devolve
 * lista vazia, e ausencia nao se distingue de inexistencia ([DA-468]): o cliente
 * tocaria numa delas e concluiria que o app perdeu as notas dele.
 *
 * ⛔ **Sem "Todas"** (decisao dele, 01/09/2026, vendo a tela publicada): a tela esta
 * sempre num mes, e nasce no mais recente. Quem escolhe os 4 e faz o corte visivel e'
 * o `SecaoNotas` — aqui chega a lista ja fatiada, pra este componente nao ter duas
 * versoes da verdade sobre quantos meses existem.
 *
 * ⚠️ A rolagem e' horizontal e a barra fica escondida: com 4 pastilhas cabe tudo em
 * qualquer celular, mas a lista **nao pode quebrar em duas linhas** — a altura da aba
 * mudaria sozinha, e o rotulo de um mes cortado nao distingue um mes do outro.
 */
function SeletorDeMes({ meses, mes, onEscolher }: {
  meses: MesDeNotasApp[];
  mes: string | null;
  onEscolher: (m: string) => void;
}): React.ReactElement | null {
  if (!meses.length) return null;

  return (
    <div
      style={{
        display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 16px 10px',
        WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
      }}
    >
      {meses.map((m) => {
        const ativa = m.mes === mes;
        return (
          <button
            key={m.mes}
            type="button"
            onClick={() => onEscolher(m.mes)}
            aria-pressed={ativa}
            style={{
              flexShrink: 0, height: 32, padding: '0 14px', borderRadius: 16,
              fontSize: 13, fontWeight: ativa ? 800 : 600, whiteSpace: 'nowrap',
              // ⚠️ A marcacao nao e' so cor: a pastilha escolhida troca de FUNDO e de
              // PESO. Verde e vermelho nao servem de sinal nesta casa, e um contorno
              // sozinho some no sol da obra.
              backgroundColor: ativa ? COBRE : '#fff',
              color: ativa ? '#fff' : '#57534e',
              border: `1px solid ${ativa ? COBRE : BORDA}`,
            }}
          >
            {rotuloMesCurto(m.mes)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * O botao que baixa as notas do mes num .zip — pedido do Ruan, 01/09/2026.
 *
 * ⛔ **Quem nao tem nota nenhuma nao ve este botao.** Ate 09/09 a regra era mais
 * dura e escondia o botao de quem so tinha CUPOM — **9 das 13 contas com documento**
 * (TELO 53, RONAN 42, RAFAEL 17, PAULO 17, WITALO 13, ERNANE 12, ZE WILTON 11,
 * HATILLA 8, VF 1), justamente aquelas pra quem o cupom e' o unico papel da compra.
 * Com o cupom dentro do pacote (10/09/2026) o botao passou a existir pra elas.
 *
 * ⚠️ **Sumiu o estado APAGADO junto** — ele so' existia pro mes que so tinha cupom, e
 * esse caso deixou de existir. Botao que nao pode mais recusar nao precisa da tela de
 * recusa; o que sobra e' o botao aceso ou botao nenhum.
 *
 * ⚠️ **O `temAlgumaNota` olha so os meses VISIVEIS**, e nao o acervo inteiro: um botao
 * que so pode ficar apagado, porque a unica nota do cliente esta num mes que a tela
 * nao oferece, e' pior que botao nenhum.
 */
function PacoteDoMes({ meses, mes }: { meses: MesDeNotasApp[]; mes: string }): React.ReactElement | null {
  const quantas = meses.find((m) => m.mes === mes)?.total ?? 0;
  if (!meses.some((m) => m.total > 0) || quantas === 0) return null;

  return (
    <div style={{ padding: '0 16px 12px' }}>
      <BotaoPacote
        caminho={caminhoPacoteNotas(mes)}
        nomeArquivo={`notas-fiscais-${rotuloMesLongo(mes)}-${mes.slice(0, 4)}.zip`}
        tituloCompartilhar={`Notas fiscais de ${rotuloMesLongo(mes)}`}
        rotulo={quantas === 1
          ? `Baixar a nota de ${rotuloMesLongo(mes)} (.zip)`
          : `Baixar as ${quantas} notas de ${rotuloMesLongo(mes)} (.zip)`}
        rotuloEnviar={quantas === 1
          ? `Enviar a nota de ${rotuloMesLongo(mes)}`
          : `Enviar as ${quantas} notas de ${rotuloMesLongo(mes)}`}
      />
    </div>
  );
}

/**
 * O botao de pacote — **um so, servindo as duas abas** (10/09/2026).
 *
 * ⛔ **Ele virou generico por INSTRUCAO dele**, e a frase foi *"o botao deve ser
 * exatamente igual a esse. Tudo igual"*, com o print do botao da aba Notas Fiscais.
 * Quando o pedido e' "identico", a garantia nao e' copiar o estilo com cuidado: e' ser
 * a MESMA peca. Copiado, o proximo ajuste num deles abriria uma diferenca que ninguem
 * pediu — que e' exatamente o que ele nao quer. O que varia entre as duas abas e' so'
 * o CAMINHO, o NOME do arquivo e o ROTULO; o resto e' identico por construcao.
 *
 * ⛔ **Nao ha copia das sutilezas aqui**: o fetch no `onPointerDown`, o `AbortError`
 * que nao vira download, a revogacao adiada da URL e o botao que fica ACESO enquanto
 * trabalha moram todos no `useArquivoCompartilhavel`, que os dois chamam.
 *
 * ⚠️ **A espera e' de OUTRA ordem de grandeza que a de uma nota**: cada PDF custa
 * ~0,7s no servidor ([DA-301]), e o pior mes medido tem 35 documentos (ILUMINAR,
 * maio/2026) — ~25 segundos. Foi com UMA nota de 2,6s que ele relatou *"achei ate que
 * o app tinha travado"*; por isso o rotulo TROCA por "Preparando as notas..." em vez
 * de so' girar um icone, e o aviso embaixo diz que pode demorar. A falha fica NA TELA,
 * colada no botao, ate a proxima tentativa.
 *
 * ⚠️ **O icone e' `Archive` (caixa de arquivo), nao a seta de download** — escolha
 * dele em 10/09/2026, e ele vale nos DOIS botoes pelo motivo do paragrafo de cima.
 *
 * ⛔ **Icone se escolhe pelo DESENHO NO TAMANHO EM QUE ELE APARECE, nunca pelo nome.**
 * O pedido dele foi "livrinhos" (mandou o icone do WinRAR), e eu publiquei o `Library`
 * do lucide — que **em 16px vira quatro riscos soltos**. Ele achou na tela: *"o simbolo
 * ficou errado"*. Pior: eu tinha escrito no proprio mockup que aquele desenho *"em 18px
 * pode ler como riscos em vez de livros"*, e escolhi assim mesmo, porque o NOME batia
 * com o pedido. ✅ O que resolveu foi renderizar os candidatos **dentro do botao, em
 * 16px, com uma copia ampliada ao lado** — ai da' pra ver que traco fino e detalhe
 * interno (a cinta atravessando os volumes) viram MALHA DE GRADE nesse tamanho, e que
 * forma fechada e simples sobrevive. Foi ele quem escolheu, vendo os desenhos.
 */
function BotaoPacote({ caminho, nomeArquivo, tituloCompartilhar, rotulo, rotuloEnviar }: {
  caminho: string;
  nomeArquivo: string;
  tituloCompartilhar: string;
  rotulo: string;
  /** O que o botao passa a dizer quando o arquivo esta pronto na mao (10/09/2026). */
  rotuloEnviar: string;
}): React.ReactElement {
  const arquivo = useArquivoCompartilhavel({
    caminho,
    nomeArquivo,
    mime: 'application/zip',
    tituloCompartilhar,
  });

  return (
    <>
      <button
        type="button"
        aria-busy={arquivo.ocupado}
        onPointerDown={() => { void arquivo.preparar(); }}
        onClick={() => { void arquivo.abrir(); }}
        disabled={arquivo.ocupado}
        style={{
          width: '100%', height: 44, borderRadius: 10, fontSize: 14, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          backgroundColor: COBRE, color: '#fff', border: 'none',
          // Nao apagar enquanto trabalha: apagado + parado e' a aparencia de quebrado.
          opacity: 1,
        }}
      >
        {arquivo.ocupado
          ? <Loader2 size={16} className="animate-spin" />
          : arquivo.pronto ? <Share2 size={16} /> : <Archive size={16} />}
        {arquivo.ocupado ? 'Preparando as notas...' : arquivo.pronto ? rotuloEnviar : rotulo}
      </button>
      {arquivo.ocupado && (
        <div style={{ fontSize: 12, color: '#78716c', marginTop: 5, lineHeight: 1.4 }}>
          Estamos montando o arquivo. Pode levar até meio minuto — não feche esta tela.
        </div>
      )}
      {/* ⚠️ **O aviso do estado PRONTO nao e' enfeite: sem ele o botao troca de nome
          sozinho e ninguem sabe por que.** O arquivo ja esta no celular, na memoria da
          pagina; o que falta e' um toque novo, porque o Safari so' abre a lista de
          envio durante o gesto — e os ~12s do preparo gastam essa janela. */}
      {/* ⛔ **Nao usar verde aqui** ([[user_daltonismo]]): o aviso de falha logo abaixo
          e' vermelho, e o par verde/vermelho nao se distingue nesta casa. Quem carrega
          o significado e' o TEXTO; a cor e' so' o cobre da marca, o mesmo do botao. */}
      {arquivo.pronto && !arquivo.ocupado && (
        <div style={{ fontSize: 12, color: COBRE, fontWeight: 700, marginTop: 5, lineHeight: 1.4 }}>
          Arquivo pronto. Toque de novo para escolher o WhatsApp, o e-mail ou salvar.
        </div>
      )}
      {/* A falha fica NA TELA ate a proxima tentativa, nao num aviso que some. */}
      {arquivo.erro && !arquivo.ocupado && (
        <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 5, lineHeight: 1.4 }}>
          {arquivo.erro}
        </div>
      )}
    </>
  );
}

/**
 * A aba "Boletos" — o papel que a loja subiu pra ele pagar ([DA-629], 27/08/2026).
 *
 * ⛔ **Nao e' a lista de contas com outro nome.** Contas a Pagar responde *"quanto eu
 * devo"*; aqui a pergunta e' *"como eu pago"*, e a resposta e' o PDF mais a linha
 * digitavel. Um titulo aparece nas duas quando tem boleto — a conta e' a divida, o
 * boleto e' o instrumento.
 *
 * ⚠️ **A linha digitavel vem PRIMEIRO que o botao de baixar**, e e' de proposito: no
 * celular, o gesto que paga e' copiar e colar no aplicativo do banco. Abrir o PDF pra
 * digitar 47 numeros e' o caminho em que a pessoa desiste — e desistir aqui significa
 * pedir o boleto no WhatsApp de novo, que e' o que esta aba existe pra evitar.
 */
function SecaoBoletos(): React.ReactElement {
  const [dados, setDados] = useState<BoletosApp | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    appApi
      .boletos()
      .then((d) => { if (vivo) setDados(d); })
      .catch((e: ErroApp) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, []);

  if (erro) return <Aviso texto={erro} />;
  if (!dados) return <Carregando />;

  // ⚠️ **O estado VAZIO e' o de quase todo mundo** (medido em 27/08/2026: das 16
  // contas do app, **uma** tem titulo em aberto), entao ele nao pode terminar sem
  // apontar nada — e' a mesma emenda que a [DA-627] cobrou na aba vizinha. A porta
  // que ele nomeia e' o chat, que e' de onde o boleto vinha antes.
  if (!dados.boletos.length) {
    return (
      <>
        <Cabecalho texto="Seus boletos" />
        <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: '20px 16px', fontSize: 15, color: '#44403c', lineHeight: 1.5 }}>
          Você não tem boleto em aberto.
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
            Quando a loja emitir um, ele aparece aqui — e você recebe um lembrete no WhatsApp no dia do vencimento.
            Precisa de um boleto agora? Fale com a gente pelo chat.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Cabecalho texto="Seus boletos" />
      <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}` }}>
        {dados.boletos.map((b) => (
          <LinhaBoleto key={b.id} boleto={b} recebedor={dados.recebedor} pagador={dados.pagador} />
        ))}
      </div>
      <div style={{ padding: '10px 16px 2px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
        O boleto sai desta lista assim que a loja registrar o pagamento.
      </div>
    </>
  );
}

function LinhaBoleto({ boleto, recebedor, pagador }: {
  boleto: BoletoApp;
  recebedor: BoletosApp['recebedor'];
  pagador: BoletosApp['pagador'];
}): React.ReactElement {
  // 🔑 O pedido dele de 27/08 resolvido sem digitar nada ([DA-630]) — o numero que o
  // cliente lanca no contas a pagar da empresa dele. A frase mora em
  // `@fiodecobre/shared` com teste proprio: texto visivel nao tem portao.
  const ref = referenciaDoBoleto(boleto.notas);
  // O valor que o BANCO vai cobrar sai da propria linha; sem ela, a soma dos titulos.
  const daLinha = lerLinhaDigitavel(boleto.linha_digitavel);
  const valor = daLinha?.valor ?? boleto.valor;
  return (
    <div style={{ padding: '13px 12px 13px 16px', borderTop: `1px solid #f0efee` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, color: '#111' }}>
            Vence {fmtData(boleto.data_vencimento)}
          </div>
          {/* ⚠️ Cor NUNCA carrega o sinal sozinha nesta casa (daltonismo): o vencido
              e' laranja **e** diz por escrito ha quantos dias. */}
          <div style={{ fontSize: 12.5, marginTop: 2, color: boleto.vencido ? LARANJA : '#6b7280', fontWeight: boleto.vencido ? 700 : 400 }}>
            {boleto.vencido
              ? `Vencido há ${boleto.dias_atraso} ${boleto.dias_atraso === 1 ? 'dia' : 'dias'}`
              : 'Em aberto'}
          </div>
          {ref && (
            <div style={{ fontSize: 12.5, marginTop: 3, color: '#44403c', lineHeight: 1.35 }}>
              {ref}
            </div>
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          R$ {fmtMoney(valor)}
        </div>
        {/* PDF opcional desde 20/10/2026: sem arquivo, sem botao — a linha paga sozinha. */}
        {boleto.tem_pdf && (
          <BotaoArquivo
            caminho={caminhoPdfBoleto(boleto.id)}
            nomeArquivo={`boleto-${boleto.id}.pdf`}
            tituloCompartilhar={`Boleto de R$ ${fmtMoney(valor)}`}
            rotuloPronto="Compartilhar o boleto"
            rotuloOcupado="Preparando o boleto"
          />
        )}
      </div>
      {/* 🔑 O que torna o PDF dispensavel (pedido dele, 20/10/2026): o cliente confere
          PARA QUEM paga antes de colar a linha no banco — protege de boleto falso. */}
      <div style={{ marginTop: 10, backgroundColor: '#faf9f8', border: `1px solid ${BORDA}`, borderRadius: 8, padding: '8px 10px', fontSize: 12.5, color: '#44403c', lineHeight: 1.55 }}>
        <div><span style={{ color: '#78716c' }}>Recebedor:</span> <b>{recebedor.nome}</b></div>
        <div><span style={{ color: '#78716c' }}>CNPJ:</span> {formatCpfCnpj(recebedor.cnpj)}</div>
        {daLinha && (
          <div><span style={{ color: '#78716c' }}>Banco:</span> {daLinha.banco} ({daLinha.banco_codigo})</div>
        )}
        {pagador?.nome && (
          <div>
            <span style={{ color: '#78716c' }}>Pagador:</span> {pagador.nome}
            {pagador.documento ? ` · ${formatCpfCnpj(pagador.documento)}` : ''}
          </div>
        )}
        <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
          Confira no seu banco se o recebedor é esse antes de pagar.
        </div>
      </div>
      {boleto.linha_digitavel && <LinhaDigitavel digitos={boleto.linha_digitavel} />}
    </div>
  );
}

/**
 * A linha digitavel com o botao de copiar — o gesto que realmente paga.
 *
 * ⚠️ **O numero aparece formatado e e' COPIADO cru.** O aplicativo do banco aceita os
 * digitos; os pontos e espacos existem pro olho conferir o comeco contra o papel.
 *
 * ⚠️ **O `navigator.clipboard` nao existe em contexto inseguro e pode ser negado** —
 * por isso ha o caminho antigo (`execCommand`) atras dele, e a falha DIZ o que fazer
 * em vez de ficar muda. Botao de copiar que nao copia e nao avisa e' pior que nao
 * ter botao: a pessoa cola o que estava na area de transferencia antes.
 */
function LinhaDigitavel({ digitos }: { digitos: string }): React.ReactElement {
  const [estado, setEstado] = useState<'parado' | 'copiado' | 'falhou'>('parado');

  // ⚠️ A mascara sai da MESMA peca que o servidor usa pra validar (`@fiodecobre/shared`)
  // — duas implementacoes do mesmo formato viram disfarce, nao conferencia.
  const formatado = formatarLinhaDigitavel(digitos);

  async function copiar(): Promise<void> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(digitos);
      } else {
        const campo = document.createElement('textarea');
        campo.value = digitos;
        campo.style.position = 'fixed';
        campo.style.opacity = '0';
        document.body.appendChild(campo);
        campo.select();
        const ok = document.execCommand('copy');
        campo.remove();
        if (!ok) throw new Error('execCommand recusou');
      }
      setEstado('copiado');
      setTimeout(() => setEstado('parado'), 2500);
    } catch {
      setEstado('falhou');
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11.5, color: '#78716c', fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>
        LINHA DIGITÁVEL
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{
          flex: 1, minWidth: 0, backgroundColor: '#faf9f8', border: `1px solid ${BORDA}`,
          borderRadius: 8, padding: '8px 10px', fontSize: 13, color: '#292524',
          // ⚠️ `break-word`, nunca `break-all`: os blocos ja vem separados por espaco,
          // entao a quebra natural cai ENTRE eles. Com `break-all` ela cairia no meio
          // de um grupo, e quem confere numero contra papel perde a conta.
          fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word', lineHeight: 1.4,
        }}>
          {formatado}
        </div>
        <button
          type="button"
          onClick={() => { void copiar(); }}
          aria-label="Copiar a linha digitável"
          style={{
            // ⚠️ Altura FIXA e ancorado no topo: em `stretch` ele esticava junto com a
            // caixa de duas linhas e virava um retangulo alto, que nao le como botao.
            flexShrink: 0, width: TAM_BOTAO, height: TAM_BOTAO, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: estado === 'copiado' ? '#166534' : COBRE, color: '#fff', border: 'none',
          }}
        >
          {estado === 'copiado' ? <Check size={17} /> : <Copy size={17} />}
        </button>
      </div>
      {/* O estado vai ESCRITO, nao so na cor do botao — e a falha diz a saida. */}
      {estado === 'copiado' && (
        <div style={{ fontSize: 12, color: '#166534', marginTop: 4, fontWeight: 600 }}>
          Copiado. Cole no aplicativo do seu banco.
        </div>
      )}
      {estado === 'falhou' && (
        <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4, lineHeight: 1.4 }}>
          Seu navegador não deixou copiar. Segure o dedo sobre os números acima para selecionar.
        </div>
      )}
    </div>
  );
}

/**
 * O botao que entrega a nota daquela conta.
 *
 * ⚠️ **O PDF nao pode sair por `<a href>`.** Ele e' servido atras do token do
 * cliente, e um link comum vai sem cabecalho nenhum — voltaria 401. Entao o arquivo
 * e' buscado por `fetch` com o `Bearer` e entregue de duas formas, nesta ordem:
 *
 *  1. `navigator.share` com o ARQUIVO — no iPhone e' o que abre "Salvar em Arquivos",
 *     imprimir e mandar pra quem ele quiser. E' o mesmo caminho ja provado no celular
 *     dele pelo `BotaoCompartilhar` da foto ([DA-524]).
 *  2. Sem suporte a compartilhar arquivo (PC, navegador antigo), baixa como arquivo
 *     comum por um link temporario de blob.
 *
 * ⚠️ **O download comeca no TOQUE (`onPointerDown`), nao no clique** — o Safari so
 * aceita `navigator.share` enquanto durar o gesto, e um `await fetch` no meio gasta
 * essa janela e volta `NotAllowedError` com cara de defeito aleatorio. Mesma licao do
 * `BotaoCompartilhar`; ⛔ nao "simplificar" movendo o fetch pro onClick.
 */
function BotaoNota({ nota }: { nota: NotaApp | NotaAcervoApp | null }): React.ReactElement {
  // ⚠️ **Sem nota, o espaco continua ocupado.** Devolver `null` faria a linha
  // encolher e a coluna de valores dancar entre uma linha e outra — o olho le isso
  // como numero desalinhado, nao como "esta faltando a nota".
  if (!nota) return <div style={{ flexShrink: 0, width: TAM_BOTAO }} aria-hidden />;
  return (
    <BotaoArquivo
      caminho={caminhoPdfNota(nota)}
      nomeArquivo={`nota-${nota.numero}.pdf`}
      tituloCompartilhar={`Nota ${nota.numero}`}
      rotuloPronto={`Compartilhar a nota ${nota.numero}`}
      rotuloOcupado={`Preparando a nota ${nota.numero}`}
    />
  );
}

interface BotaoArquivoProps {
  caminho: string;
  nomeArquivo: string;
  tituloCompartilhar: string;
  rotuloPronto: string;
  rotuloOcupado: string;
}

/** O que a busca do arquivo devolve. ⚠️ `erro` e' a frase que o SERVIDOR mandou —
 *  quando ela existe, ela e' melhor que qualquer texto generico nosso (o pacote
 *  recusa dizendo *"este mes so tem cupom fiscal"*, e isso o cliente entende). */
interface ArquivoBaixado {
  arquivo: File | null;
  erro: string | null;
}

/**
 * O nucleo compartilhado — extraido do `BotaoNota` em 27/08/2026, quando o boleto
 * passou a precisar do mesmo gesto, e virado HOOK em 01/09/2026, quando o pacote de
 * notas passou a precisar dele com outra CARA (botao largo com rotulo, e nao o
 * quadradinho de 36px).
 *
 * ⛔ **Extrair foi obrigatorio, nao capricho**: as sutilezas abaixo (o fetch no
 * `onPointerDown`, o `AbortError` que nao vira download, a revogacao adiada da URL,
 * o botao que fica ACESO enquanto trabalha) foram todas pagas com defeito em campo.
 * Duplicar isso significaria que a proxima correcao pegaria uma copia so.
 *
 * ⚠️ **A promessa guardada e' amarrada ao CAMINHO.** No botao da nota o componente
 * morre junto com a linha, entao guardar so a promessa bastava; o botao do pacote
 * troca de URL quando o cliente troca de mes, e sem essa amarra ele entregaria o zip
 * do mes anterior com o nome do mes novo.
 */
function useArquivoCompartilhavel({ caminho, nomeArquivo, mime = 'application/pdf', tituloCompartilhar }: {
  caminho: string;
  nomeArquivo: string;
  mime?: string;
  tituloCompartilhar: string;
}): {
  ocupado: boolean; erro: string | null; pronto: boolean;
  preparar: () => Promise<ArquivoBaixado>; abrir: () => Promise<void>;
} {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // ⚠️ **O arquivo pronto mora num REF, e nao so no estado** (10/09/2026): o clique que
  // envia precisa chamar `navigator.share` SEM nenhum `await` antes (ver `abrir`), e
  // `await` de promessa — mesmo ja resolvida — cede o turno. O ref le sincrono.
  const prontoRef = useRef<File | null>(null);
  const [pronto, setPronto] = useState(false);
  const arquivoRef = useRef<{ caminho: string; promessa: Promise<ArquivoBaixado> } | null>(null);

  function preparar(): Promise<ArquivoBaixado> {
    if (arquivoRef.current?.caminho !== caminho) {
      // Trocou de alvo: o que estava pronto e' de outro arquivo.
      prontoRef.current = null;
      setPronto(false);
      arquivoRef.current = {
        caminho,
        promessa: (async (): Promise<ArquivoBaixado> => {
          try {
            const token = getTokenApp();
            const resp = await fetch(caminho, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) {
              // A recusa costuma DIZER o motivo, em portugues de cliente. Trocar isso
              // por um texto nosso apagaria a unica informacao util da resposta.
              let motivo: string | null = null;
              try { motivo = ((await resp.json()) as { erro?: string }).erro ?? null; } catch { motivo = null; }
              return { arquivo: null, erro: motivo };
            }
            const blob = await resp.blob();
            return { arquivo: new File([blob], nomeArquivo, { type: mime }), erro: null };
          } catch {
            return { arquivo: null, erro: null };
          }
        })(),
      };
    }
    return arquivoRef.current.promessa;
  }

  function salvarComoArquivo(arquivo: File): void {
    const url = URL.createObjectURL(arquivo);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revogar na hora corta o download em alguns navegadores; um respiro basta.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  /** O seletor nativo. ⚠️ Devolve `'nao-abriu'` em vez de baixar por conta propria —
   *  quem decide o plano B e' o `abrir`, que sabe se ainda ha um segundo toque. */
  async function tentarCompartilhar(arquivo: File): Promise<'foi' | 'desistiu' | 'nao-abriu'> {
    const nav = navigator as Navigator & {
      share?: (d: { files?: File[]; title?: string }) => Promise<void>;
      canShare?: (d: { files?: File[] }) => boolean;
    };
    if (!nav.share || !nav.canShare?.({ files: [arquivo] })) return 'nao-abriu';
    try {
      // ⛔ Nenhum `await` pode existir ANTES desta linha no caminho do 2o toque.
      await nav.share({ files: [arquivo], title: tituloCompartilhar });
      return 'foi';
    } catch (e) {
      // Desistir do seletor nao e' falha — insistir com um download seria fazer o
      // que ele acabou de recusar.
      return (e as Error | undefined)?.name === 'AbortError' ? 'desistiu' : 'nao-abriu';
    }
  }

  /**
   * ⛔ **DOIS CAMINHOS, e o de cima existe por causa da janela do toque.**
   *
   * O Safari so' abre o seletor de compartilhamento enquanto durar a ativacao do
   * gesto, e ela nao sobrevive aos ~12s que o `.zip` leva pra nascer (17 DANFEs a
   * ~0,7s cada). Por isso, quando o arquivo JA esta pronto, este caminho chama o
   * `share` **sincronamente**, na mesma pilha do clique: ler o `prontoRef` nao cede o
   * turno, e um `await preparar()` — mesmo com a promessa ja resolvida — cederia.
   *
   * ⚠️ **Foi ELE quem mediu isto, usando** (10/09/2026): o `.zip` caia na tela de
   * download do Safari e ele completava a mao (*"dela eu envio para whatsapp"*), com o
   * print do seletor nativo **aberto com o proprio ZIP** — o que prova que o formato
   * passa e que o que faltava era so' a janela do gesto. ⚠️ E o botao de UMA nota
   * sempre funcionou de primeira: 2,6s cabem na janela, 12s nao.
   */
  async function abrir(): Promise<void> {
    // ── 2o toque: o arquivo esta na mao, entao o seletor abre direto ──
    const jaPronto = prontoRef.current;
    if (jaPronto) {
      const r = await tentarCompartilhar(jaPronto);
      if (r === 'foi' || r === 'desistiu') return;
      // Nem com o gesto fresco o seletor abriu: nao ha terceiro toque que ajude.
      salvarComoArquivo(jaPronto);
      setErro('Seu aparelho não abriu a lista de envio, então baixamos o arquivo.');
      return;
    }

    if (ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const { arquivo, erro: motivo } = await preparar();
      if (!arquivo) {
        // ⚠️ Falha de download nao pode ficar muda nem culpar o documento do cliente:
        // ele esta certo, quem falhou fomos nos. E o proximo toque tenta de novo
        // — por isso a promessa guardada e' descartada aqui.
        arquivoRef.current = null;
        setErro(motivo ?? 'Não conseguimos abrir agora. Tente de novo.');
        return;
      }

      // Arquivo rapido (o PDF de uma nota, ~2,6s): a janela do gesto ainda vale e o
      // seletor abre no PRIMEIRO toque, como sempre abriu. ⛔ Nao trocar isto por
      // "sempre dois toques": seria cobrar um toque a mais de quem nunca precisou.
      const r = await tentarCompartilhar(arquivo);
      if (r === 'foi' || r === 'desistiu') return;

      // ⛔ **Aqui NAO se baixa** — este e' o caso do pacote demorado. O arquivo esta
      // pronto na memoria; o que falta e' um gesto novo. Baixar agora seria jogar o
      // cliente na tela do Safari, que e' exatamente o desvio que ele pediu pra tirar.
      prontoRef.current = arquivo;
      setPronto(true);
    } finally {
      setOcupado(false);
    }
  }

  return { ocupado, erro, pronto, preparar, abrir };
}

function BotaoArquivo({ caminho, nomeArquivo, tituloCompartilhar, rotuloPronto, rotuloOcupado }: BotaoArquivoProps): React.ReactElement {
  const { ocupado, erro, pronto, preparar, abrir } = useArquivoCompartilhavel({ caminho, nomeArquivo, tituloCompartilhar });

  return (
    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      {/* ⚠️ **O fetch comeca no `onPointerDown`, o resto no `onClick`** — o Safari so
          aceita `navigator.share` enquanto durar o gesto, e um `await` no meio gasta
          essa janela e volta `NotAllowedError` com cara de defeito aleatorio.
          ⛔ Nao juntar os dois num `onClick` so.
          ⚠️ O icone e' `Share2` porque o app inteiro ja fala esse dialeto
          (`BotaoCompartilhar` do chat, o PDF do orcamento) — o `Share` de caixa-e-seta
          e' usado so pra ILUSTRAR o botao do Safari na instrucao de instalar, e
          reusa-lo aqui misturaria "compartilhe" com "aquele botao ali do navegador". */}
      {/* 🔴 **O BOTAO TEM QUE DIZER QUE ESTA TRABALHANDO** (20/08/2026). O Ruan
          compartilhou uma nota da ILUMINAR e relatou: *"Demorou muito para abrir a
          lista de opcoes, eu achei ate que o app tinha travado"*. Medido depois: o
          PDF leva **2,6s** pra nascer no servidor (Puppeteer), e no celular ainda
          somam a rede e os 212KB. O sinal de "estou indo" era `opacity: 0.55` num
          quadrado de 36px — invisivel. Dois segundos e meio de nada, num botao que
          nao muda, LEEM como travamento; com o rodinha girando, leem como espera.
          ⛔ O `disabled` sozinho nao serve de aviso: ele impede o segundo toque (que
          e' bom) e nao comunica nada. */}
      <button
        type="button"
        aria-label={ocupado ? rotuloOcupado : rotuloPronto}
        aria-busy={ocupado}
        onPointerDown={() => { void preparar(); }}
        onClick={() => { void abrir(); }}
        disabled={ocupado}
        style={{
          width: TAM_BOTAO, height: TAM_BOTAO, borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: COBRE, color: '#fff', border: 'none',
          // ⚠️ Nao apagar o botao enquanto trabalha: apagado + parado e' exatamente
          // a aparencia de quebrado. Ele fica ACESO, so trocando o icone.
          opacity: 1,
        }}
      >
        {ocupado ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />}
      </button>
      {/* ⚠️ **O estado PRONTO tambem chega aqui, e ele nao pode ficar mudo** (10/09/2026).
          Com o PDF de uma nota o seletor abre no primeiro toque — 2,6s cabem na janela
          do gesto —, entao isto quase nunca acende; mas quando acender (rede lenta,
          Puppeteer em fila) o botao ficaria identico e o toque pareceria nao ter feito
          nada. Um botao de 36px nao tem onde escrever, entao a frase usa o mesmo slot
          do erro, logo abaixo. */}
      {pronto && !ocupado && !erro && (
        <div style={{ fontSize: 11, color: COBRE, fontWeight: 700, marginTop: 4, textAlign: 'right', maxWidth: 128, lineHeight: 1.3 }}>
          Toque de novo para enviar
        </div>
      )}
      {/* Falha nao pode ficar muda nem culpar a nota do cliente — e o texto mora
          colado no botao que a produziu, nao num aviso no topo da tela. */}
      {erro && (
        <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4, textAlign: 'right', maxWidth: 128, lineHeight: 1.3 }}>
          {erro}
        </div>
      )}
    </div>
  );
}
